import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { Router } from "@angular/router";
import { NavGroup } from "./models/nav-group";
import { NavItem } from "./models/nav-item";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { UserService } from "./../../shared/services/user.service";
import { McHttpService } from "./../../shared/services/mc-http.service";
import { NavbarRefreshService } from "../shared/navbar-refresh.service";

@Injectable()
export class NavService {
  private _navGroups: NavGroup[] = [];
  navGroups$ = new BehaviorSubject<NavGroup[]>([]);
   private messageCount = 0;

  private userFlags = {
    isCredentialed: false,
    hasExpiredCredentials: false,
    isInGrace: false,
    isBadPayment: false,
    isSubjectToCMP: false,
    revokedCRT: false,
    provisional: false,
    hasRT: false,
    isUnderNBRCJE: false,
    hasHoursAndCanRecertify: false,
  };
  private refreshSubject = new Subject<void>();
  
    // Observable that components can subscribe to
    refresh$ = this.refreshSubject.asObservable();

  constructor(
    private appSettings: AppSettingsService,
    private userService: UserService,
    private mcService: McHttpService,
    private router: Router,
    private viewState: ViewStateService,
    private navbarRefreshService: NavbarRefreshService
  ) {
    
    this.userService.user$.subscribe((user) => {
      this.updateUserFlags(user);
      this.loadNav(); // trigger nav rebuild when user updates
    });

    this.loadMessageCount();

    this.navbarRefreshService.refresh$.subscribe((_user) => {
        this.userService.user$.subscribe((user) => {
        //console.log('Sidebar refresh triggered');
        this.updateUserFlags(user);
        this.loadNav(); // whatever method reloads this data in the sidebar, you just call again!
      });      
    });
  }

  get NavGroups(): NavGroup[] {
    return this._navGroups;
  }

  private updateUserFlags(user: any): void {
    const flags = this.userFlags;

    if (!user) {
      Object.keys(flags).forEach((key) => (flags[key] = false));
    } else {
      flags.isCredentialed = !!user.credentialHolder;
      flags.isBadPayment = !!user.hasBadPayment;
      flags.isInGrace = !!user.credentialHolderExpiredGracePeriod;
      flags.isSubjectToCMP = !!user.subjectToCMP;
      flags.revokedCRT = !!user.revokedCRT;
      flags.hasExpiredCredentials = !!user.hasExpiredCredentials;
      flags.provisional = !!user.provisional;
      flags.hasRT = !!user.hasRT;
      flags.isUnderNBRCJE = !!user.underNBRCJE;
      flags.hasHoursAndCanRecertify = !!user.hasHoursAndCanRecertify;

      // override credentialed status if bad payment
      if (flags.isBadPayment) {
        flags.isCredentialed = false;
      }
      //console.log(flags);
    }
  }

  private loadMessageCount(): void {
    this.mcService.getNotificationCount().subscribe((tabs) => {
      this.messageCount = this.parseMessageCount(tabs);
      this.loadNav();
      this.viewState.stopLoading();
    });
  }

  private parseMessageCount(tabs: any[]): number {
    return tabs?.find((t) => t.n === 'All')?.c ?? 0;
  }

  private loadNav(): void {
    this._navGroups = this.generateNavGroups();
    this.navGroups$.next(this._navGroups);
  }

  private generateNavGroups(): NavGroup[] {
    const f = this.userFlags;
    const renewalText = f.isSubjectToCMP ? "Pay Annual CMP" : "Pay Annual Invoice";

    return [
      new NavGroup([
        new NavItem("Home", ""),
        new NavItem(
          "Message Center",
          "message-center",
          null,
          !f.isUnderNBRCJE && (f.isCredentialed || f.hasExpiredCredentials),
          null,
          this.messageCount
        ),
        new NavItem("News", null, "https://nbrc.org/news", !f.isUnderNBRCJE),
      ], true),

      new NavGroup([
        new NavItem("Credential Maintenance", null, null, !f.provisional && !f.isUnderNBRCJE),
        new NavItem("Recertify Now", "purchase/checkout/certification", null, f.hasHoursAndCanRecertify && !f.isBadPayment, null, null, 1),
        new NavItem("Enter my CEUs", "practitioner/credentials-maintenance/default", null, !f.provisional && !f.isUnderNBRCJE && !f.isBadPayment && f.isSubjectToCMP, null, null, 1),
        new NavItem("Go to Assessments", "assessment", null, !f.provisional && f.hasRT && !f.isBadPayment, null, null, 1),
        new NavItem("CMP Content Outlines", null, "https://www.nbrc.org/resources/contentoutlines/#toggle-id-1", !f.isUnderNBRCJE, null, null, 1),
        new NavItem("Submit Feedback", null, "https://nbrc.org/cmpSurvey", !f.isUnderNBRCJE, null, null, 1),
      ], f.isCredentialed || f.isInGrace),

      new NavGroup([
        new NavItem(renewalText, "purchase/annual-renewal", null, f.isCredentialed && !f.revokedCRT && !f.provisional),
        new NavItem("Credential Verifications", "purchase/credential-verification", null, (f.isCredentialed || f.hasExpiredCredentials) && !f.isBadPayment),
        new NavItem("Order Products", "purchase/products", null, f.isCredentialed && !f.provisional && !f.isBadPayment),
        new NavItem("My Documents", "practitioner/mydocuments"),
      ], !f.isUnderNBRCJE),

      new NavGroup([
        new NavItem("Self-Assessment Exams", null, this.appSettings.ExternalLinks.purchaseSAE),
        new NavItem("Free Practice Exam", null, this.appSettings.ExternalLinks.practiceExam),
      ], !f.isUnderNBRCJE),

      new NavGroup([
        new NavItem("Content Outlines", null, this.appSettings.ExternalLinks.contentOutlines),
        new NavItem("Candidate Handbook", null, this.appSettings.ExternalLinks.candidateHandbook),
        new NavItem("FAQ's", null, "https://www.nbrc.org/resources/faqs/"),
        new NavItem("Interested in Volunteering", null, "https://www.nbrc.org/interested-volunteer", f.isCredentialed),
      ], !f.isUnderNBRCJE),

      new NavGroup([
        new NavItem("My Account", "practitioner/profile/edit"),
        new NavItem("Change Password", "auth/change-password"),
        new NavItem("Sign Out", "auth/sign-out"),
      ], true)
    ];
  }
}
